import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronRight, Check, Calendar as CalIcon, ArrowRight } from "lucide-react";
import { api, PROPERTY_IMAGES, ATMOSPHERIC_IMAGES, DETAIL_IMAGES } from "@/lib/api";
import { Reveal, Counter } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/lib/useSEO";

const HomePage = () => {
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const { t, lang } = useLang();

  useSEO({
    title: lang === "en" ? "Holiday home in Castel di Sangro" : "Casa vacanza a Castel di Sangro",
    description: lang === "en"
      ? "La dimora di Patty — an elegant holiday home in Castel di Sangro, in the heart of the Abruzzo Apennines. A few minutes from the Roccaraso ski slopes and the Abruzzo National Park."
      : "La dimora di Patty — casa vacanza elegante a Castel di Sangro, nel cuore dell'Appennino abruzzese. A pochi minuti dalle piste di Roccaraso e dal Parco Nazionale d'Abruzzo.",
    keywords: "casa vacanza Castel di Sangro, casa vacanze Abruzzo, affitto Roccaraso, appartamento Castel di Sangro, alloggio Parco Nazionale d'Abruzzo",
  });

  useEffect(() => {
    api.get("/property").then((r) => setProperty(r.data));
    api.get("/reviews").then((r) => setReviews(r.data || []));
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0 ken-burns">
          <img
            src={ATMOSPHERIC_IMAGES.heroTerrace}
            alt="Castel di Sangro — vista aerea del borgo con l'Appennino abruzzese"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-black/15" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 md:pb-24 w-full">
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] tracking-tight max-w-4xl animate-fade-up">
            {t("hero.headline_1")}<br /><em className="text-accent not-italic">{t("hero.headline_2")}</em>
          </h1>
          <p className="text-white/80 mt-6 max-w-xl text-base sm:text-lg leading-relaxed animate-fade-up" style={{ animationDelay: "200ms" }}>
            {t("hero.subtitle")}
          </p>

          <div
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-xl border border-accent/30 animate-fade-up"
            style={{ animationDelay: "300ms" }}
            data-testid="hero-cin"
          >
            <span className="overline text-accent tracking-wider">CIN</span>
            <span className="w-px h-3 bg-white/30" />
            <span className="text-white/90 text-sm tracking-wider">IT066028C2GFSS7WQS</span>
          </div>

          <div className="mt-10 animate-fade-up" style={{ animationDelay: "400ms" }}>
            <Link
              to="/booking"
              data-testid="hero-cta-booking"
              className="btn-shimmer group inline-flex items-center gap-5 pl-6 pr-3 py-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/25 hover:bg-white/20 transition-all"
            >
              <div className="text-left">
                <div className="overline text-accent">{t("hero.cta_overline")}</div>
                <div className="flex items-center gap-2 text-white text-base mt-1">
                  <CalIcon size={16} className="text-accent" />
                  <span>{t("hero.cta_label")}</span>
                </div>
              </div>
              <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center group-hover:bg-white transition-colors">
                <ArrowRight size={18} className="text-accent-foreground group-hover:text-foreground transition-colors" />
              </div>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-12 mt-12 text-white/85 animate-fade-up" style={{ animationDelay: "600ms" }}>
            <div>
              <div className="font-serif text-4xl">
                <Counter to={property?.rating || 4.9} suffix="" />
                <span className="text-2xl">/5</span>
              </div>
              <div className="overline text-white/60 mt-1">{t("hero.stat_rating")}</div>
            </div>
            <div className="h-12 w-px bg-white/30" />
            <div>
              <div className="font-serif text-4xl">
                <Counter to={property?.review_count || 127} />
              </div>
              <div className="overline text-white/60 mt-1">{t("hero.stat_reviews")}</div>
            </div>
            <div className="h-12 w-px bg-white/30" />
            <div>
              <div className="font-serif text-4xl">
                <Counter to={2300} suffix="+" />
              </div>
              <div className="overline text-white/60 mt-1">{t("hero.stat_nights")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6">
            <div className="overline text-accent gold-line mb-6">{t("story.overline")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
              {t("story.title_1")}<br /><em className="text-accent not-italic">{t("story.title_2")}</em>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-5">
              {t("story.p1")}
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              {t("story.p2")}
            </p>
            <div className="grid grid-cols-2 gap-6 mt-10">
              {[
                { v: 65, suf: " mq", l: t("story.stat_surface") },
                { v: 5, l: t("story.stat_guests") },
                { v: 2, l: t("story.stat_bedrooms") },
                { v: 1, l: t("story.stat_bathrooms") },
              ].map((s) => (
                <div key={s.l} className="border-l border-accent pl-4">
                  <div className="font-serif text-3xl"><Counter to={s.v} suffix={s.suf || ""} /></div>
                  <div className="overline text-muted-foreground mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={150}>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img src={PROPERTY_IMAGES.esterno} alt="L'edificio della dimora a Castel di Sangro" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-12 -left-12 w-48 h-48 hidden md:block rounded-2xl overflow-hidden border-8 border-background shadow-xl">
                <img src={PROPERTY_IMAGES.cucina} alt="Cucina" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -top-8 -right-8 hidden md:block">
                <div className="bg-foreground text-background px-6 py-5 rounded-xl shadow-xl">
                  <div className="overline text-accent mb-1">{t("story.price_from")}</div>
                  <div className="font-serif text-3xl">€95<span className="text-base text-background/70">{t("story.per_night")}</span></div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-16">
            <div className="overline text-accent gold-line-center mb-6">{t("gallery.overline")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl">{t("gallery.title")}</h2>
          </Reveal>

          <div className="grid grid-cols-12 gap-3 md:gap-4">
            {[
              { src: PROPERTY_IMAGES.camera, label: t("room.camera_name"), span: "col-span-12 md:col-span-7 row-span-2 aspect-[16/12] md:aspect-auto md:h-[560px]" },
              { src: PROPERTY_IMAGES.cucina, label: t("room.cucina_name"), span: "col-span-6 md:col-span-5 h-[270px]" },
              { src: PROPERTY_IMAGES.bagno, label: t("room.bagno_name"), span: "col-span-6 md:col-span-5 h-[270px]" },
              { src: PROPERTY_IMAGES.cameretta, label: t("room.cameretta_name"), span: "col-span-6 md:col-span-6 h-[280px]" },
              { src: PROPERTY_IMAGES.ingresso, label: t("gallery.cat_interior"), span: "col-span-6 md:col-span-6 h-[280px]" },
            ].map((img, i) => (
              <Reveal key={i} delay={i * 80} className={`${img.span} relative overflow-hidden rounded-2xl group cursor-pointer`}>
                <img src={img.src} alt={img.label} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-5 left-5 text-white translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                  <div className="overline text-accent">{t("gallery.cat_interior")}</div>
                  <div className="font-serif text-2xl">{img.label}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/gallery"
              data-testid="home-view-gallery"
              className="inline-flex items-center gap-2 text-foreground hover:text-accent transition-colors"
            >
              {t("gallery.view_all")} <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* AMENITIES */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <div className="overline text-accent gold-line mb-6">{t("amenities.overline")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
              {t("amenities.title_1")}<br />{t("amenities.title_2")}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {t("amenities.desc")}
            </p>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={150}>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {[
                "amenities.wifi", "amenities.kitchen", "amenities.tv", "amenities.heating",
                "amenities.hairdryer", "amenities.courtesy", "amenities.linen", "amenities.cleaning",
              ].map((k) => (
                <div key={k} className="flex items-center gap-3 py-3 border-b border-border">
                  <Check size={16} className="text-accent" />
                  <span className="text-foreground/80">{t(k)}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* DETAILS — piccole attenzioni */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-3xl mb-16">
            <div className="overline text-accent gold-line mb-6">{t("details.overline")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
              {t("details.title_1")}<br /><em className="text-accent not-italic">{t("details.title_2")}</em>
            </h2>
            <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
              {t("details.desc")}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { src: DETAIL_IMAGES.welcomeKit, k: "details.card", d: "details.card_desc", tall: true },
              { src: DETAIL_IMAGES.breakfast, k: "details.welcome", d: "details.welcome_desc" },
              { src: DETAIL_IMAGES.coffee, k: "details.coffee", d: "details.coffee_desc" },
              { src: DETAIL_IMAGES.slippers, k: "details.slippers", d: "details.slippers_desc" },
              { src: DETAIL_IMAGES.courtesy, k: "details.courtesy", d: "details.courtesy_desc", tall: true },
              { src: DETAIL_IMAGES.dispenser, k: "details.dispenser", d: "details.dispenser_desc" },
              { src: DETAIL_IMAGES.bidet, k: "details.courtesy", d: "details.courtesy_desc" },
              { src: DETAIL_IMAGES.sanitized, k: "details.sanitized", d: "details.sanitized_desc" },
              { src: DETAIL_IMAGES.art, k: "details.art", d: "details.art_desc" },
            ].map((it, i) => (
              <Reveal key={i} delay={i * 60}>
                <article className="group h-full bg-background border border-border rounded-2xl overflow-hidden hover:border-accent/50 transition-colors">
                  <div className={`overflow-hidden ${it.tall ? "aspect-[4/5]" : "aspect-[4/3]"}`}>
                    <img
                      src={it.src}
                      alt={t(it.k)}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <div className="overline text-accent mb-2">{t("details.overline")}</div>
                    <h3 className="font-serif text-xl md:text-2xl mb-2 leading-tight">{t(it.k)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(it.d)}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 md:py-32 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="overline text-accent gold-line mb-6">{t("reviews.overline")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
                {t("reviews.title_1")}<br /><em className="text-accent not-italic">{t("reviews.title_2")}</em>
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={20} className="fill-accent text-accent" />
              ))}
              <span className="text-2xl font-serif ml-2">4.9</span>
              <span className="text-background/60 text-sm">· {t("reviews.count")}</span>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((r, i) => (
              <Reveal key={r.id} delay={i * 120}>
                <div className="bg-background/5 backdrop-blur p-8 rounded-2xl border border-background/10 h-full hover:border-accent/40 transition-colors">
                  <div className="flex gap-1 mb-4">
                    {[...Array(r.rating)].map((_, k) => (
                      <Star key={k} size={14} className="fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="font-serif text-xl leading-relaxed text-background/90 mb-6">"{r.text}"</p>
                  <div className="text-sm">
                    <div className="text-background">{r.guest_name}</div>
                    <div className="text-background/55 overline mt-1">{r.country}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ATMOSPHERIC_IMAGES.exterior} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <Reveal>
            <div className="overline text-accent gold-line-center mb-8">{t("cta.overline")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-tight mb-8">
              {t("cta.title_1")}<br /><em className="text-accent not-italic">{t("cta.title_2")}</em>
            </h2>
            <Link
              to="/booking"
              data-testid="home-cta-book"
              className="btn-shimmer inline-block px-12 py-4 rounded-full bg-accent text-accent-foreground hover:bg-background hover:text-foreground transition-colors text-sm tracking-wide"
            >
              {t("cta.button")}
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
